import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ReceiptText, ShieldCheck } from "lucide-react";

const EFFECT_LABELS = {
  EXPENSE_INVOICE_CREATED: "Gider faturası kaydedilecek",
  SUPPLIER_DEBT_RECORDED: "Tedarikçi borcu oluşturulacak",
  PAYMENT_PENDING: "Ödeme bekliyor olarak izlenecek",
  NO_STOCK_EFFECT: "Stok hareketi oluşturulmayacak",
  STOCK_ENTRY_ADDED: "Stok girişi oluşturulacak",
  PRODUCT_DRAFTS_CREATED: "Yeni ürün kararı önizlemede (ürün henüz kaydedilmez)",
  NEW_SUPPLIER_CREATED: "Yeni tedarikçi kararı önizlemede (tedarikçi henüz kaydedilmez)",
  EXPENSE_NOT_RECORDED_EXTERNAL: "Gider kaydı dış sistem tarafından yönetiliyor",
  SUPPLIER_DEBT_NOT_RECORDED_EXTERNAL: "Cari borç dış sistem tarafından yönetiliyor",
};

const ISSUE_LABELS = {
  LINE_CLASS_UNRESOLVED: "Satır türünü seçin",
  PRODUCT_KIND_REQUIRED: "Satırın ürün mü hizmet mi olduğunu seçin",
  EXTRA_CONFIRMATION_REQUIRED: "Kaynak bilgileri için ek onay gerekiyor",
  WRITER_NOT_READY: "İçeri alma altyapısı henüz hazır değil",
  WAREHOUSE_REQUIRED: "Depo seçimi gerekiyor",
  WAREHOUSE_INVALID: "Seçilen depo kullanılamıyor (pasif, silinmiş, başka hesaba ait ya da sağlayıcı deposu); başka bir depo seçin",
  STOCK_NOT_APPLICABLE: "Bu faturada stoklanacak satır yok",
  SUPPLIER_NOT_MATCHED: "Tedarikçi eşleştirmesi gerekiyor",
  PRODUCT_NOT_MATCHED: "Ürün eşleştirmesi gerekiyor",
  PRODUCT_DRAFT_CANNOT_BE_STOCKED: "Yeni ürün stoğa alınmadan önce kataloglanmalı",
  TOTAL_MISMATCH: "Fatura toplamı satırlardan hesaplanan tutarla uyuşmuyor",
  LINE_TOTAL_MISMATCH: "Satır tutarı miktar × fiyat − iskonto ile uyuşmuyor",
  LINE_DISCOUNT_UNREADABLE: "Satır iskontosu okunamadı",
  LINE_FIELD_MISSING: "Satırda miktar, fiyat, KDV veya açıklama eksik",
  PRODUCT_MATCH_AMBIGUOUS: "Birden fazla ürünle eşleşiyor; seçim gerekiyor",
  STOCK_PRODUCT_UNRESOLVED: "Stok için ürün eşleşmesi gerekiyor",
  SUPPLIER_MATCH_AMBIGUOUS: "Birden fazla tedarikçiyle eşleşiyor; otomatik birleştirilmez",
  EXEMPTION_REQUIRED: "KDV istisna kodu gerekiyor",
  EXEMPTION_CODE_REQUIRED: "KDV istisna kodu gerekiyor",
  DOCUMENT_NUMBER_REQUIRED: "Fatura numarası eksik",
  SOURCE_CHANGED: "Kaynak fatura değişti; önizleme yenilenmeli",
  PRIOR_IMPORT_FAILED: "Bu fatura için önceki içeri alma denemesi incelemede",
  OWNERSHIP_UNCERTAIN: "Muhasebe sahipliği doğrulanamadı",
  NOT_ENABLED: "İçeri alma bu hesap için açık değil",
  TOTALS_INVALID: "Tutarlar hesaplanamadı",
  LINE_VAT_DIFFERS_FROM_UBL: "Satır KDV'si UBL'deki tutardan farklı",
  LINE_VAT_MISMATCH: "Satır KDV'si UBL ile uyuşmuyor; bu fatura içeri alınamaz",
  CATALOG_DUPLICATE: "Bu kayıt katalogda zaten var; yeniden oluşturulamaz",
  SUPPLIER_CHOICE_INVALID: "Seçilen tedarikçi bu faturanın vergi numarasıyla eşleşmiyor veya güncel değil",
  REFERENCE_INVALID: "Seçilen kayıt artık geçerli değil; listeyi yenileyip yeniden seçin",
  PRODUCT_DRAFT_DUPLICATE: "Aynı kalem için ikinci kez yeni ürün kararı verilemez",
  LINE_NET_DIFFERS_FROM_UBL: "Satır tutarı UBL'deki tutardan farklı",
};

const UNIT_FALLBACK = "Birim yok";
const SUPPLIER_STATE = { matched: "Eşleşti", create: "Yeni tedarikçi oluşturulacak", unmatched: "Eşleşmedi", ambiguous: "Belirsiz" };
const PRODUCT_STATE = { matched: "Ürün eşleşti", create: "Yeni ürün önerildi", unmatched: "Ürün eşleşmedi", ambiguous: "Ürün eşleşmesi belirsiz", not_applicable: null };
const EMPTY_CHOICE = { kind: null, product_id: null, new_product_draft: false, stock: null, vat_exemption_code: null };

function label(key) {
  return ISSUE_LABELS[key] || `Ek bilgi gerekiyor (${String(key || "").toLowerCase().replace(/_/g, " ")})`;
}

function quantity(value, unit) {
  const text = formatDecimal(value, { minFraction: 0, trimZeros: true });
  if (text == null) return "—";
  return `${text} ${unit?.label || unit?.code || UNIT_FALLBACK}`;
}

// Kaynak ayrımı: UBL'nin beyanı "(UBL)" etiketiyle; bizim hesabımız "hesaplanan" olarak ve yalnız farklıysa ya da UBL değeri yoksa.
function SourcedAmount({ provider, computed, currency }) {
  if (provider == null) {
    return <span>UBL'de yok · hesaplanan {money(computed, currency)}</span>;
  }
  const differs = computed != null && formatDecimal(provider) !== formatDecimal(computed);
  return <span>{money(provider, currency)} (UBL){differs ? <><br /><span className="text-xs text-amber-900">hesaplanan {money(computed, currency)}</span></> : null}</span>;
}

// Sunucu tutarları Decimal METNİ olarak gelir; `Number` dönüşümü büyük/hassas değerlerde basamak kaybettirir.  Biçimlendirme yalnız metin
// üzerinde yapılır: binlik ".", ondalık ",", en az `minFraction` hane (fazla haneler korunur).  Geçersiz değer = null (tahmin yok).
export function formatDecimal(value, { minFraction = 2, trimZeros = false } = {}) {
  let raw;
  if (typeof value === "number") raw = Number.isFinite(value) && Math.abs(value) < 1e15 ? String(value) : "";
  else if (typeof value === "string") raw = value.trim();
  else return null;
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(raw);
  if (!match) return null;
  const [, sign, intRaw, fracRaw = ""] = match;
  const integer = intRaw.replace(/^0+(?=\d)/, "");
  let fraction = trimZeros ? fracRaw.replace(/0+$/, "") : fracRaw;
  if (fraction.length < minFraction) fraction = fraction.padEnd(minFraction, "0");
  const zero = /^0+$/.test(integer + (fracRaw || "0"));
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${sign && !zero ? "-" : ""}${grouped}${fraction ? `,${fraction}` : ""}`;
}

function money(value, currency) {
  const text = formatDecimal(value);
  if (text == null) return "—";
  return `${text} ${currency === "TRY" ? "₺" : currency || ""}`.trim();
}

function absDecimal(value) {
  return typeof value === "string" ? value.trim().replace(/^-/, "") : value;
}

function payload(mode, lineChoices, createSupplier = false, supplierId = null, warehouseId = null) {
  return { mode, warehouse_id: mode === "expense_and_stock" ? warehouseId : null, supplier_id: supplierId,
    create_supplier: supplierId ? false : createSupplier, line_choices: lineChoices };
}

function DocumentPreview({ view, decision, onKind, onProduct, onNewProduct, stockSelected, onStock, createSupplier, onCreateSupplier, onSupplier, onWarehouse }) {
  const doc = view.document || {};
  const supplier = view.supplier || {};
  const decisions = view.decisions || {};
  const lineItems = (index) => [...(decision?.review_items || []), ...(decision?.blocker_items || [])]
    .filter((item) => item?.line_index === index).map((item) => item.key);
  return (
    <div className="space-y-4" data-testid="document-preview">
      <div className="grid gap-3 rounded-xl border border-border bg-muted/30 p-4 sm:grid-cols-3">
        <div><p className="text-xs text-muted-foreground">Fatura no</p><p className="font-semibold">{doc.number || "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Tarih</p><p className="font-semibold">{doc.issue_date ? new Date(`${doc.issue_date}T00:00:00`).toLocaleDateString("tr-TR") : "—"}</p></div>
        <div><p className="text-xs text-muted-foreground">Yasal toplam</p><p className="font-semibold" data-testid="legal-total">{money(doc.legal_payable, doc.currency)}</p></div>
      </div>

      <div className="rounded-xl border border-border p-4" data-testid="supplier-field">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground">Tedarikçi</p>
            <p className="truncate font-semibold">{supplier.name || "—"}</p>
            {supplier.tax_number ? <p className="text-xs text-muted-foreground">VKN/TCKN {supplier.tax_number}</p> : null}
          </div>
          <span className="rounded-md border px-2 py-0.5 text-xs">{SUPPLIER_STATE[supplier.state] || "—"}</span>
        </div>
        {(supplier.issues || []).map((key) => <p key={key} className="mt-2 text-sm text-amber-900">• {label(key)}</p>)}
        {(supplier.candidates || []).length && (supplier.state !== "matched" || supplier.selected_id) ? <div className="mt-3 space-y-1 text-sm" data-testid="supplier-candidates">
          <p className="text-xs text-muted-foreground">Bu vergi numarasıyla kayıtlı tedarikçiler (otomatik seçilmez):</p>
          <div className="flex flex-wrap gap-2">
            {supplier.candidates.map((c) => (
              <Button key={c.id} size="sm" variant={supplier.selected_id === c.id ? "default" : "outline"} aria-pressed={supplier.selected_id === c.id}
                onClick={() => onSupplier(supplier.selected_id === c.id ? null : c.id)}>{c.label || "Adsız tedarikçi"}</Button>
            ))}
          </div>
        </div> : null}
        {supplier.state !== "matched" ? <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
          {decisions.supplier?.create_new?.available && !(supplier.candidates || []).length ? (
            <Button size="sm" variant={createSupplier ? "default" : "outline"} aria-pressed={createSupplier} onClick={() => onCreateSupplier(!createSupplier)}>Yeni tedarikçi kararını önizle</Button>
          ) : null}
          {createSupplier ? <span className="text-xs text-muted-foreground">Bu yalnız önizlemedir; tedarikçi kaydedilmez.</span> : null}
          {!decisions.supplier?.match_existing?.available ? <span className="text-muted-foreground">Bu vergi numarasıyla kayıtlı tedarikçi bulunamadı.</span> : null}
        </div> : null}
      </div>

      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
            <tr><th className="p-2">Kalem</th><th className="p-2 text-right">Miktar</th><th className="p-2 text-right">Birim fiyat</th><th className="p-2 text-right">İskonto</th><th className="p-2 text-right">KDV</th><th className="p-2 text-right">Tutar</th></tr>
          </thead>
          <tbody>
            {(view.lines || []).map((line) => {
              const issues = [...new Set([...(line.issues || []), ...lineItems(line.index)])];
              return (
                <tr key={line.index} className="border-t align-top" data-testid={`line-${line.index}`}>
                  <td className="p-2">
                    <p className="font-medium">{line.description || "Açıklama yok"}</p>
                    {PRODUCT_STATE[line.product?.state] ? <p className="text-xs text-muted-foreground">{PRODUCT_STATE[line.product.state]}</p> : null}
                    <div className="mt-1 flex flex-wrap gap-1">
                      <Button size="sm" variant={line.kind === "service" ? "default" : "outline"} aria-pressed={line.kind === "service"} onClick={() => onKind(line.index, "service")}>Hizmet</Button>
                      <Button size="sm" variant={line.kind === "product" ? "default" : "outline"} aria-pressed={line.kind === "product"} onClick={() => onKind(line.index, "product")}>Ürün</Button>
                      {line.kind === "service" ? <>
                        <Button size="sm" variant="outline" disabled aria-disabled="true">Kalemi hatırla (sonraki aşamada)</Button>
                        <span className="self-center text-xs text-muted-foreground">Yalnız fatura satırı olarak kalır. Katalog kaydı sonraki aşamada; bu ekranda ürün kaydedilmez.</span>
                      </> : null}
                    </div>
                    {line.kind === "product" ? <div className="mt-1 space-y-1" data-testid={`product-choice-${line.index}`}>
                      {(line.product?.candidates || []).length ? <div className="flex flex-wrap gap-1">
                        {line.product.candidates.map((c) => (
                          <Button key={c.id} size="sm" variant={line.product.selected_id === c.id ? "default" : "outline"} aria-pressed={line.product.selected_id === c.id}
                            onClick={() => onProduct(line.index, line.product.selected_id === c.id ? null : c.id)}>{c.label || "Adsız ürün"}{c.sku ? ` · ${c.sku}` : ""}</Button>
                        ))}
                      </div> : null}
                      <div className="flex flex-wrap items-center gap-1">
                        <Button size="sm" variant={line.product?.remember_selected ? "default" : "outline"} aria-pressed={!!line.product?.remember_selected}
                          onClick={() => onNewProduct(line.index, !line.product?.remember_selected)}>Yeni ürün kararını önizle</Button>
                        <span className="text-xs text-muted-foreground">Bu yalnız önizlemedir; ürün kaydedilmez. Kalemi kataloğa hatırlama sonraki aşamada.</span>
                      </div>
                    </div> : null}
                    {issues.map((key) => <p key={key} className="mt-1 text-xs text-amber-900" role="note">• {label(key)}</p>)}
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">{quantity(line.quantity, line.unit)}</td>
                  <td className="p-2 text-right whitespace-nowrap">{money(line.unit_price, doc.currency)}</td>
                  <td className="p-2 text-right whitespace-nowrap">{line.discount ? money(line.discount, doc.currency) : "—"}</td>
                  <td className="p-2 text-right whitespace-nowrap">{line.vat_rate != null ? `%${line.vat_rate}` : "—"}<br /><span className="text-xs"><SourcedAmount provider={line.vat_amount_provider} computed={line.vat_amount_computed} currency={doc.currency} /></span></td>
                  <td className="p-2 text-right whitespace-nowrap"><SourcedAmount provider={line.line_extension_provider} computed={line.net_computed} currency={doc.currency} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border border-border p-4 text-sm" data-testid="stock-decision">
        <p className="font-medium">Stok</p>
        {decisions.stock_action?.available ? <div className="mt-2 flex gap-2">
          <Button size="sm" variant={stockSelected ? "default" : "outline"} aria-pressed={stockSelected} onClick={() => onStock(true)}>Stok girişi yap</Button>
          <Button size="sm" variant={!stockSelected ? "default" : "outline"} aria-pressed={!stockSelected} onClick={() => onStock(false)}>Stok girişi yapma</Button>
        </div> : <p className="mt-1 text-muted-foreground">Ürün satırı olmadığı için stok etkisi yok; depo sorulmaz.</p>}
        {decisions.warehouse?.required ? <div className="mt-3 space-y-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-amber-900" data-testid="warehouse-decision">
          <p>Stok girişi için depo seçin. Depo seçilmeden onay kapalı kalır; varsayılan depo kendiliğinden kullanılmaz.</p>
          {decisions.warehouse.selection_available ? <div className="flex flex-wrap gap-2" data-testid="warehouse-options">
            {(decisions.warehouse.options || []).map((o) => (
              <Button key={o.id} size="sm" variant={decisions.warehouse.selected_id === o.id ? "default" : "outline"} aria-pressed={decisions.warehouse.selected_id === o.id}
                onClick={() => onWarehouse(decisions.warehouse.selected_id === o.id ? null : o.id)}>{o.outside_list ? `${o.name} (seçili; listenin dışında)` : o.name}</Button>
            ))}
            {!(decisions.warehouse.options || []).length ? <span>Bu hesapta seçilebilecek aktif depo yok.</span> : null}
          </div> : <p>Depo listesi şu an alınamıyor; depo seçilemez.</p>}
          {decisions.warehouse.selection_available && decisions.warehouse.options_complete === false ? <p>Liste eksik: yalnız ilk 50 depo gösteriliyor, hesaptaki tüm depolar değildir.</p> : null}
          {decisions.warehouse.default_warehouse ? <div className="rounded-md border border-amber-300 bg-white/60 p-2" data-testid="default-warehouse">
            <p>Varsayılan depo: <strong>{decisions.warehouse.default_warehouse.name}</strong> (otomatik seçilmez)</p>
            <p className="text-xs">Seçilirse beklenen stok: {(decisions.warehouse.default_warehouse.expected_stock || []).map((e) => `Satır ${e.line + 1}: ${quantity(e.quantity, { code: e.unit, label: e.unit === "C62" ? "Adet" : e.unit })}`).join(", ") || "—"}</p>
            {decisions.warehouse.selected_id !== decisions.warehouse.default_warehouse.id ? (
              <Button size="sm" variant="outline" className="mt-1" onClick={() => onWarehouse(decisions.warehouse.default_warehouse.id)}>Bu depoyu seç</Button>
            ) : null}
          </div> : null}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled aria-disabled="true">Depoyu sonra seç</Button>
            <span className="text-xs">Sonraki aşamada; şu an kullanılamaz.</span>
          </div>
        </div> : null}
      </div>

      <div className="rounded-xl border border-border p-4 text-sm" data-testid="payment-state">
        <p className="font-medium">Ödeme durumu: Ödenecek</p>
        <p className="mt-1 text-muted-foreground">Bu ekranda ödeme kaydı, kasa veya banka hareketi oluşturulmaz. Ödeme ayrıca hesap, tarih ve tutar seçilerek eklenir.</p>
      </div>
    </div>
  );
}

export default function QuickImportDialog({ open, onClose, invoice, onCompleted, confirmEnabled = false }) {
  const [mode, setMode] = useState("expense_only");
  const [lineChoices, setLineChoices] = useState([]);
  const [createSupplier, setCreateSupplier] = useState(false);
  const [supplierId, setSupplierId] = useState(null);
  const [warehouseId, setWarehouseId] = useState(null);
  const [decision, setDecision] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const requestSequence = useRef(0);

  const documentId = String(invoice?.id || "").trim();
  const load = useCallback(async (nextMode = mode, nextChoices = lineChoices, nextCreate = createSupplier, nextSupplier = supplierId, nextWarehouse = warehouseId) => {
    if (!documentId) return;
    const sequence = ++requestSequence.current;
    setLoading(true);
    try {
      const result = await api.post(`/incoming-invoices/${encodeURIComponent(documentId)}/quick-import/preview`, payload(nextMode, nextChoices, nextCreate, nextSupplier, nextWarehouse));
      if (requestSequence.current === sequence) setDecision(result);
    } catch (error) {
      if (requestSequence.current === sequence) {
        setDecision(null);
        toast.error(error?.message || "İçeri alma önizlemesi hazırlanamadı");
      }
    } finally {
      if (requestSequence.current === sequence) setLoading(false);
    }
  }, [documentId, lineChoices, mode, createSupplier, supplierId, warehouseId]);

  useEffect(() => {
    if (!open || !documentId) {
      requestSequence.current += 1;
      return undefined;
    }
    setMode("expense_only");
    setLineChoices([]);
    setCreateSupplier(false);
    setSupplierId(null);
    setWarehouseId(null);
    setDecision(null);
    load("expense_only", [], false, null, null);
    return () => { requestSequence.current += 1; };
  }, [open, documentId]); // eslint-disable-line react-hooks/exhaustive-deps

  const unresolved = useMemo(() => {
    const rows = [...(decision?.review_items || []), ...(decision?.blocker_items || [])]
      .filter((item) => Number.isInteger(item?.line_index));
    return [...new Set(rows.map((item) => item.line_index))].sort((a, b) => a - b);
  }, [decision]);

  const view = decision?.document_view || null;
  const withChoice = (index, patch) => {
    const next = [...lineChoices];
    while (next.length <= index) next.push({ ...EMPTY_CHOICE });
    next[index] = { ...next[index], ...patch };
    return next;
  };
  const chooseKind = (index, kind) => {
    // Hizmet satırında ürün kimliği, ürün taslağı ve stok çelişkilidir (sunucu reddeder): türle birlikte temizlenir.
    const next = withChoice(index, kind === "service" ? { kind, product_id: null, new_product_draft: false, stock: null } : { kind, stock: mode === "expense_and_stock" ? true : null });
    setLineChoices(next);
    load(mode, next);
  };
  const productIndexes = (view?.lines || []).filter((l) => l.kind === "product").map((l) => l.index);
  const chooseStock = (on) => {
    let next = [...lineChoices];
    const nextMode = on ? "expense_and_stock" : "expense_only";
    for (const index of productIndexes) {
      while (next.length <= index) next.push({ ...EMPTY_CHOICE });
      next[index] = { ...next[index], kind: "product", stock: on ? true : null };
    }
    if (!on) next = next.map((c) => ({ ...c, stock: c.stock === true ? null : c.stock }));
    setMode(nextMode);
    setLineChoices(next);
    if (!on) setWarehouseId(null);                      // stok kapanınca depo seçimi de bırakılır
    load(nextMode, next, createSupplier, supplierId, on ? warehouseId : null);
  };
  const chooseCreateSupplier = (on) => {
    setCreateSupplier(on);
    setSupplierId(null);
    load(mode, lineChoices, on, null);
  };
  // Seçim yalnız sunucunun döndürdüğü adaylardan; sunucu yeniden doğrular (tenant, aktiflik, vergi no).
  const chooseSupplier = (id) => {
    setSupplierId(id);
    setCreateSupplier(false);
    load(mode, lineChoices, false, id);
  };
  // Depo seçimi yalnız sunucunun listelediği seçeneklerden (ya da açıkça gösterilen varsayılandan); sunucu her önizlemede yeniden doğrular.
  const chooseWarehouse = (id) => {
    setWarehouseId(id);
    load(mode, lineChoices, createSupplier, supplierId, id);
  };
  const chooseProduct = (index, id) => {
    const next = withChoice(index, { kind: "product", product_id: id, new_product_draft: false });
    setLineChoices(next);
    load(mode, next);
  };
  const chooseNewProduct = (index, on) => {
    const next = withChoice(index, { kind: "product", product_id: null, new_product_draft: on });
    setLineChoices(next);
    load(mode, next);
  };

  const confirm = async () => {
    if (!confirmEnabled || !decision?.can_confirm || decision?.document_view_error || !documentId) return;
    setConfirming(true);
    try {
      const result = await api.post(`/incoming-invoices/${encodeURIComponent(documentId)}/quick-import/confirm`, {
        preview_fingerprint: decision.preview_fingerprint,
        choices_digest: decision.choices_digest,
        decision_digest: decision.decision_digest,
        ...payload(mode, lineChoices, createSupplier, supplierId, warehouseId),
      });
      if (result?.status === "APPLIED") toast.success("Fatura giderlere işlendi");
      else toast.success("İçeri alma işlemi başlatıldı");
      onCompleted?.(result);
      onClose?.();
    } catch (error) {
      toast.error(error?.message || "Fatura içeri alınamadı");
    } finally {
      setConfirming(false);
    }
  };

  const allIssues = decision?.blocker_items?.length ? decision.blocker_items : (decision?.review_items || []);
  // Belge görünümü varken satıra bağlı nedenler ilgili satırın yanında gösterilir; burada yalnız belge düzeyindekiler kalır.
  const issues = view ? allIssues.filter((item) => !Number.isInteger(item?.line_index)) : allIssues;

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value && !confirming) onClose?.(); }}>
      <DialogContent className={view ? "max-h-[90vh] max-w-4xl overflow-y-auto" : "max-w-2xl"}>
        <DialogHeader>
          <DialogTitle>Faturayı içeri al</DialogTitle>
          <DialogDescription>Oluşacak gider, cari borç ve stok etkilerini onaylamadan önce kontrol edin.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {!view ? <div className="rounded-xl border border-border bg-muted/30 p-4">
            <div className="flex items-start gap-3">
              <ReceiptText className="mt-0.5 h-5 w-5 text-cyan-700" />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">{invoice?.invoice_no || invoice?.uuid || "Gelen fatura"}</p>
                <p className="truncate text-sm text-muted-foreground">{invoice?.sender_name || invoice?.supplier_name || "Tedarikçi"}</p>
              </div>
              <p className="font-semibold">{money(decision?.document_summary?.payable, decision?.document_summary?.currency)}</p>
            </div>
          </div> : null}

          {decision?.document_summary?.reconciliation?.status === "mismatch" ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
              Fatura toplamı ile satırlardan hesaplanan tutar arasında {money(absDecimal(decision.document_summary.reconciliation.difference), decision.document_summary.currency)} fark var
              (hesaplanan {money(decision.document_summary.reconciliation.computed_payable, decision.document_summary.currency)}). Bu fatura hızlı akışla içeri alınamaz.
            </div>
          ) : null}

          {!view ? <div className="rounded-lg border border-cyan-200 bg-cyan-50/70 px-3 py-2 text-sm text-cyan-950">
            Bu hızlı akış faturayı gider ve cari borç olarak kaydeder. Stoklu ürün faturaları için gelişmiş işlemi kullanabilirsiniz.
          </div> : null}

          {loading ? <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Önizleme hazırlanıyor</div> : null}

          {!loading && decision?.document_view_error ? <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="alert">
            Fatura satırları güvenle gösterilemedi. Kaynak belge ile önizleme uyuşmuyor; bu fatura şimdilik içeri alınamaz.
          </div> : null}

          {!loading && decision && view ? <DocumentPreview view={view} decision={decision} onKind={chooseKind} onProduct={chooseProduct} onNewProduct={chooseNewProduct} onSupplier={chooseSupplier} onWarehouse={chooseWarehouse}
            stockSelected={mode === "expense_and_stock"} onStock={chooseStock}
            createSupplier={createSupplier} onCreateSupplier={chooseCreateSupplier} /> : null}

          {!loading && decision ? <>
            {!view && unresolved.map((index) => (
              <div key={index} className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/70 p-3">
                <span className="text-sm font-medium text-amber-950">Satır {index + 1} türü</span>
                <div className="flex gap-2">
                  <Button size="sm" aria-pressed={lineChoices[index]?.kind === "service"} variant={lineChoices[index]?.kind === "service" ? "default" : "outline"} onClick={() => chooseKind(index, "service")}>Hizmet</Button>
                  <Button size="sm" aria-pressed={lineChoices[index]?.kind === "product"} variant={lineChoices[index]?.kind === "product" ? "default" : "outline"} onClick={() => chooseKind(index, "product")}>Ürün</Button>
                </div>
              </div>
            ))}

            {decision.effect_summary?.length ? <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="mb-2 flex items-center gap-2 font-semibold text-emerald-900"><ShieldCheck className="h-4 w-4" /> Onayladığınızda</div>
              <ul className="space-y-1 text-sm text-emerald-950">{decision.effect_summary.map((effect) => <li key={effect.key}>• {EFFECT_LABELS[effect.key] || "Muhasebe kaydı hazırlanacak"}</li>)}</ul>
            </div> : null}

            {issues.length ? <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 text-sm text-amber-950">
              {issues.map((item, index) => <p key={`${item.key}-${item.line_index ?? index}`}>• {view ? label(item.key) : (ISSUE_LABELS[item.key] || "Bu fatura için ek bilgi gerekiyor")}{Number.isInteger(item?.line_index) ? ` (satır ${item.line_index + 1})` : ""}</p>)}
            </div> : null}
            {!confirmEnabled && (decision?.business_ready || decision?.can_confirm) ? <div className="rounded-lg border border-sky-200 bg-sky-50/70 p-3 text-sm text-sky-950">
              Önizleme hazır. Finansal kayıt onayı pilot aşamasında ayrıca açılacak.
            </div> : null}
          </> : null}

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="ghost" onClick={onClose} disabled={confirming}>Vazgeç</Button>
            <Button onClick={confirm} disabled={loading || confirming || !confirmEnabled || !decision?.can_confirm || !!decision?.document_view_error}>
              {confirming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} İçeri al
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
